import type { ExcelHeaderData, MDAResultsRow } from 'src/components/multidimensional-allocation/hooks/useMDAResultsData';
import { ExecutionStatus } from 'src/models/multidimensional-allocation/ExecutionStatus';
import type {
    GetMultiAllocationWorksheetExecutionStatusResponse,
    MultiAllocationWorksheetDataSourceExecutionStatus,
} from 'src/models/multidimensional-allocation/MultiAllocationWorksheetExecution';

// Header and row labels written by the CalculationService's Aggregated Totals sheet.
export const TOTAL_ALLOCATION_HEADER = 'Total Allocation';
export const UNALLOCATED_HEADER = 'Unallocated';
export const UNALLOCATED_AMOUNT_ROW_LABEL = 'Unallocated Amount';

// Blank cells arrive as '' from the parser and must count as 0, not poison the sum with NaN.
const asNumber = (cell: string | number | undefined): number => {
    const n = typeof cell === 'number' ? cell : Number(cell);
    return Number.isFinite(n) ? n : 0;
};

const CENTS_PER_UNIT = 100;

/**
 * Sum of "Total Allocation" over all rows plus the "Unallocated" cell of the "Unallocated Amount" row,
 * rounded to cents. undefined when a header or that row is missing, so callers render "-" instead of a wrong number.
 */
export function computeExecutionTotal(rows: MDAResultsRow[], headers: ExcelHeaderData[]): number | undefined {
    const totalIdx = headers.findIndex(h => h.value === TOTAL_ALLOCATION_HEADER);
    const unallocatedIdx = headers.findIndex(h => h.value === UNALLOCATED_HEADER);
    if (totalIdx < 0 || unallocatedIdx < 0) {
        return undefined;
    }
    const unallocatedRow = rows.find(r => r.row[0] === UNALLOCATED_AMOUNT_ROW_LABEL);
    if (!unallocatedRow) {
        return undefined;
    }
    const allocated = rows.reduce((sum, r) => sum + asNumber(r.row[totalIdx]), 0);
    // Cells are doubles, so a raw sum carries binary artefacts (0.1 + 0.2 = 0.30000000000000004).
    return Math.round((allocated + asNumber(unallocatedRow.row[unallocatedIdx])) * CENTS_PER_UNIT) / CENTS_PER_UNIT;
}

/** Latest status row per datasourceId by executionTime; ties or undefined times keep the first seen. */
export function pickLatestPerDatasource(
    statuses?: MultiAllocationWorksheetDataSourceExecutionStatus[]
): Map<string, MultiAllocationWorksheetDataSourceExecutionStatus> {
    const latest = new Map<string, MultiAllocationWorksheetDataSourceExecutionStatus>();
    (statuses ?? []).forEach(status => {
        const current = latest.get(status.datasourceId);
        if (!current || (status.executionTime ?? -Infinity) > (current.executionTime ?? -Infinity)) {
            latest.set(status.datasourceId, status);
        }
    });
    return latest;
}

/**
 * Why a worksheet execution failed: its own error, else each data source's latest error, where the status API puts
 * Calc's reason. undefined when the run did not fail or gives no reason.
 */
export function executionErrorOf(response?: GetMultiAllocationWorksheetExecutionStatusResponse | null): string | undefined {
    if (response?.executionError) {
        return response.executionError;
    }
    if (response?.executionStatus !== ExecutionStatus.FAILED
        && response?.executionStatus !== ExecutionStatus.PARTIALLY_FAILED) {
        return undefined;
    }
    const reasons = Array.from(pickLatestPerDatasource(response.datasourceStatuses).values())
        .map(status => status.executionError)
        .filter((error): error is string => !!error);
    return reasons.length > 0 ? Array.from(new Set(reasons)).join('; ') : undefined;
}
